// Pure berekening voor weeknetting: overuren en min-uren per week (spec 33 fase 1).
//
// Doorbetaalde afwezigheid (verlof, ziekte, feestdagen) die meetelt voor de
// CAO-norm verlaagt de effectieve weeknorm, zodat overuren niet onterecht
// worden weggestreept door afwezigheidsuren. Stijl volgt werkbon-hours.ts +
// uren-calculations.ts: pure functies, JSDoc in het Nederlands, geen externe
// afhankelijkheden, defensieve afhandeling van ongeldige invoer.

/** Rond af op 2 decimalen om floating-point drift te voorkomen. */
function round2(x: number): number {
  return Math.round(x * 100) / 100
}

/** Zet een niet-eindige of negatieve waarde veilig op 0. */
function clamp(x: number): number {
  return Number.isFinite(x) && x > 0 ? x : 0
}

/** Invoer voor de week-overwerk-berekening. */
export interface WeekOverwerkInput {
  /** Som van de werkelijk gewerkte uren in de week. */
  gewerkteUren: number
  /** Doorbetaalde afwezigheidsuren die meetellen voor de weeknorm
   *  (bijv. verlof, ziekte, feestdagen). */
  meetellendeAfwezigeUren: number
  /** Contracturen per week (pro-rata bij parttime). */
  weeknorm: number
}

/** Resultaat van de week-overwerk-berekening. */
export interface WeekOverwerkResultaat {
  /** Overuren in de week (≥ 0, afgerond op 2 decimalen). */
  overuren: number
  /** Min-uren in de week (≥ 0, afgerond op 2 decimalen). */
  minuren: number
}

/**
 * Bereken over- en min-uren voor een volledige week.
 *
 * Effectieve norm = max(0, weeknorm − meetellendeAfwezigeUren).
 * Doorbetaalde afwezigheid verlaagt de norm: een medewerker die maandag
 * verlof had hoeft dinsdag-vrijdag zijn volledige contractnorm niet in te
 * halen voordat overuren worden erkend.
 *
 * Defensief: niet-eindige of negatieve invoerwaarden worden vóór berekening
 * geklampt op 0 (identiek aan de clamping in werkbon-hours.ts).
 *
 * Formules:
 *   effectieveNorm = max(0, weeknorm − meetellendeAfwezigeUren)
 *   overuren       = round2(max(0, gewerkteUren − effectieveNorm))
 *   minuren        = round2(max(0, effectieveNorm − gewerkteUren))
 */
export function berekenWeekOverwerkEnMinuren(
  input: WeekOverwerkInput,
): WeekOverwerkResultaat {
  const gewerkteUren = clamp(input.gewerkteUren)
  const meetellendeAfwezigeUren = clamp(input.meetellendeAfwezigeUren)
  const weeknorm = clamp(input.weeknorm)

  const effectieveNorm = Math.max(0, weeknorm - meetellendeAfwezigeUren)
  const overuren = round2(Math.max(0, gewerkteUren - effectieveNorm))
  const minuren = round2(Math.max(0, effectieveNorm - gewerkteUren))

  return { overuren, minuren }
}

// ─── Week-overzicht samenvatting (#31a — verlof/ziekte als werkuren) ─────────

/** Invoer voor de week-overzicht-samenvatting. */
export interface WeekUrenSamenvattingInput {
  /** Werkelijk gewerkte uren in de week (GESLOTEN day-entries). */
  gewerkteUren: number
  /** Doorbetaalde afwezigheid die MEETELT voor de weeknorm (per BV-beleid). */
  meetellendeAfwezigeUren: number
  /** Contracturen per week (pro-rata bij parttime). */
  weeknorm: number
}

/** Uitkomst van de week-overzicht-samenvatting. */
export interface WeekUrenSamenvatting {
  gewerkteUren: number
  meetellendeAfwezigeUren: number
  weeknorm: number
  /**
   * Werkuren + meetellende doorbetaalde afwezigheid. Dit is het getal dat het
   * week-overzicht toont wanneer verlof/ziekte "als werkuren/dag" meetellen —
   * gemeten tegen de VOLLE weeknorm.
   */
  totaalInclusiefAfwezigheid: number
  /** Week-net overuren (≥ 0). */
  overuren: number
  /** Week-net min-uren (≥ 0). */
  minuren: number
}

/**
 * #31a — Bouw de week-overzicht-samenvatting zónder dubbeltelling.
 *
 * Het week-overzicht wil verlof/ziekte als werkuren/dag tonen. Tegelijk
 * verlaagt diezelfde doorbetaalde afwezigheid de effectieve weeknorm
 * (`berekenWeekOverwerkEnMinuren`). Naïef beide toepassen zou dubbel tellen.
 *
 * De HERIJKING gebruikt de algebraïsche identiteit:
 *
 *   gewerkt  vs  (weeknorm − afwezig)   ≡   (gewerkt + afwezig)  vs  weeknorm
 *
 * Daarom:
 *   - `overuren`/`minuren` komen ONGEWIJZIGD uit `berekenWeekOverwerkEnMinuren`
 *     (gewerkt tegen de verlaagde norm) — de boekingsbasis verandert niet.
 *   - `totaalInclusiefAfwezigheid` (= gewerkt + afwezig) wordt tegen de VOLLE
 *     norm gepresenteerd, niet nóg een keer tegen de verlaagde norm.
 *
 * Zo telt de afwezigheid exact één keer mee. De begeleidende test bewijst dat
 * beide formuleringen dezelfde overuren/minuren geven.
 */
export function berekenWeekUrenSamenvatting(
  input: WeekUrenSamenvattingInput,
): WeekUrenSamenvatting {
  const gewerkteUren = clamp(input.gewerkteUren)
  const meetellendeAfwezigeUren = clamp(input.meetellendeAfwezigeUren)
  const weeknorm = clamp(input.weeknorm)

  const { overuren, minuren } = berekenWeekOverwerkEnMinuren({
    gewerkteUren,
    meetellendeAfwezigeUren,
    weeknorm,
  })

  return {
    gewerkteUren: round2(gewerkteUren),
    meetellendeAfwezigeUren: round2(meetellendeAfwezigeUren),
    weeknorm: round2(weeknorm),
    totaalInclusiefAfwezigheid: round2(gewerkteUren + meetellendeAfwezigeUren),
    overuren,
    minuren,
  }
}

// ─── ATV-bewuste uren-splitsing (FO-031) ─────────────────────────────────────

/** Invoer voor de basis-/ATV-opbouw-/overuren-splitsing. */
export interface UrenSplitsingInput {
  /** Totaal werkelijk gewerkte uren in de week (= weekstaat `totalUren`). */
  gewerkteUren: number
  /**
   * Week-net uren BOVEN de effectieve norm, zoals `berekenWeekOverwerkEnMinuren`
   * die al berekent (snapshot `totalOveruren`). Dit is de ENIGE bron van het
   * "boven de norm"-bedrag; de splitsing herverdeelt dit getal en herberekent
   * het niet — zo blijft de lijst identiek aan de weekstaat-detail/verloning.
   */
  bovenNormUren: number
  /**
   * ATV-opbouw-band in uren (bv. 38 -> 40 = 2) wanneer de medewerker ATV
   * opbouwt; 0 zonder ATV-opbouw. De eerste `atvBandUren` boven de norm zijn
   * ATV-opbouw i.p.v. overuren (CAO Metaal & Techniek).
   */
  atvBandUren: number
  /**
   * Week-net min-uren (PO-besluit 2026-07-02, ATV-band-fix). Sinds het week-net
   * overwerk tegen de effectieve WERKuren meet (38+2-ATV'er -> norm 40) bevat
   * `bovenNormUren` de ATV-band niet meer. Week-net-callers (weekstaat-detail,
   * uren-lijst, periode) geven `minuren` mee; de splitsing reconstrueert dan de
   * effectieve norm (E = gewerkt + minuren − bovenNorm) en rekent de ATV-band
   * voor WEERGAVE terug uit de gewerkte uren. Zonder `minuren` (undefined)
   * geldt het legacy-gedrag: band = eerste `atvBandUren` van `bovenNormUren`
   * (live per-dag-callers zoals de huidige-week-tegels).
   */
  minuren?: number
}

/** Resultaat van de uren-splitsing (samen = `gewerkteUren`). */
export interface UrenSplitsing {
  /** Basis-uren tot de (effectieve) norm. */
  basisUren: number
  /** ATV-opbouw: het deel van de boven-norm-uren dat in de ATV-band valt. */
  atvOpbouwUren: number
  /** Echte overuren: boven de ATV-band. */
  overuren: number
}

/**
 * FO-031 — splits de gewerkte week-uren in basis / ATV-opbouw / overuren,
 * ATV-bewust en ZONDER dubbeltelling.
 *
 * De overuren-drempel respecteert de ATV-opbouw per medewerker: zonder
 * ATV-opbouw tellen overuren vanaf de norm (38); met ATV-opbouw gaan de eerste
 * `atvBandUren` (38 -> 40) naar ATV-opbouw en starten de overuren pas daarboven
 * (40). Voorbeelden uit het FO:
 *   - 41,25 gewerkt, norm 38, ATV-band 2 -> basis 38, ATV-opbouw 2, overuren 1,25.
 *   - 41,25 gewerkt, norm 38, geen ATV    -> basis 38, ATV-opbouw 0, overuren 3,25.
 *   - 49,50 gewerkt, norm 38, geen ATV    -> basis 38, ATV-opbouw 0, overuren 11,50.
 *
 * Invariant: `basisUren + atvOpbouwUren + overuren === gewerkteUren` (de
 * splitsing herverdeelt alleen, ze verandert het totaal-gewerkt niet). `basis`
 * wordt als laatste uit het verschil berekend zodat de invariant exact sluit.
 */
export function berekenUrenSplitsing(input: UrenSplitsingInput): UrenSplitsing {
  const gewerkteUren = clamp(input.gewerkteUren)
  const bovenNorm = Math.min(clamp(input.bovenNormUren), gewerkteUren)
  const atvBand = clamp(input.atvBandUren)

  if (input.minuren !== undefined) {
    // Week-net-pad (ATV-band-fix, PO 2026-07-02): `bovenNormUren` meet tegen de
    // effectieve WERKuren-norm (band-inclusief), dus de band zit er niet in.
    // Reconstrueer de effectieve norm en reken de band terug uit de gewerkte
    // uren: het stuk tussen (norm − band) en de norm is ATV-opbouw.
    const minuren = clamp(input.minuren)
    const effectieveNorm = Math.max(0, gewerkteUren + minuren - bovenNorm)
    const basisGrens = Math.max(0, effectieveNorm - atvBand)
    const atvOpbouwUren = round2(Math.min(atvBand, Math.max(0, gewerkteUren - basisGrens)))
    const overuren = round2(bovenNorm)
    const basisUren = round2(Math.max(0, gewerkteUren - atvOpbouwUren - overuren))
    return { basisUren, atvOpbouwUren, overuren }
  }

  const atvOpbouwUren = round2(Math.min(atvBand, bovenNorm))
  const overuren = round2(Math.max(0, bovenNorm - atvOpbouwUren))
  // Basis als sluitpost: gewerkt − ATV-opbouw − overuren. Met 2-decimale invoer
  // sluit de invariant hierdoor exact (geen 0,01-drift).
  const basisUren = round2(Math.max(0, gewerkteUren - atvOpbouwUren - overuren))

  return { basisUren, atvOpbouwUren, overuren }
}

// ─── Anomalie-signalering (spec 33 fase 4) ──────────────────────────────────

/** Grenzen voor weekstaat-anomaliesignalering, als percentage van de norm. */
export interface WeekstaatAnomalieGrenzen {
  /** Ondergrens (% van de norm); ratio < deze waarde = "te laag". null = niet bewaken. */
  ondergrensProcent: number | null
  /** Bovengrens (% van de norm); ratio > deze waarde = "te hoog". null = niet bewaken. */
  bovengrensProcent: number | null
  signaleerOndergrens: boolean
  signaleerBovengrens: boolean
}

/** Invoer voor de anomalie-evaluatie (week-net-uitkomst + grenzen). */
export interface WeekstaatAnomalieInput {
  /** Werkelijk gewerkte uren in de week (= weekstaat totalUren). */
  gewerkteUren: number
  /** Overuren in de week (week-net, ≥ 0). */
  overuren: number
  /** Min-uren in de week (week-net, ≥ 0). */
  minuren: number
  grenzen: WeekstaatAnomalieGrenzen
}

export type WeekstaatAnomalieCode = 'UREN_TE_LAAG' | 'UREN_TE_HOOG'

/** Eén gedetecteerde afwijking. */
export interface WeekstaatAnomalie {
  code: WeekstaatAnomalieCode
  /** Gewerkt als percentage van de effectieve norm (afgerond op 2 decimalen). */
  ratioProcent: number
  /** De overschreden grens (%). */
  grensProcent: number
  /** De effectieve weeknorm waartegen gemeten is. */
  effectieveNorm: number
}

/**
 * Bepaal of de ureninzet van een afgesloten week buiten de ingestelde grenzen
 * valt. De effectieve norm wordt gereconstrueerd uit de week-net-uitkomst:
 *
 *   effectieveNorm = gewerkteUren + minuren − overuren
 *
 * (minuren en overuren zijn nooit beide > 0, dus dit geeft exact de norm terug
 * die `berekenWeekOverwerkEnMinuren` hanteerde). Doorbetaalde afwezigheid die
 * de norm verlaagde telt zo correct mee: een legitieme verlof-/ziekteweek geeft
 * géén vals "te laag"-signaal.
 *
 * Grenzen zijn percentages van die norm; een null-grens of een uitgeschakelde
 * toggle wordt niet bewaakt. Bij een norm ≤ 0 (geen werkverwachting) wordt
 * niets gesignaleerd. Onder- en bovengrens sluiten elkaar uit (ratio is één
 * getal), dus er komt hooguit één anomalie uit.
 */
export function evalueerWeekstaatAnomalieen(
  input: WeekstaatAnomalieInput,
): WeekstaatAnomalie[] {
  const gewerkteUren = clamp(input.gewerkteUren)
  const overuren = clamp(input.overuren)
  const minuren = clamp(input.minuren)
  const effectieveNorm = round2(gewerkteUren + minuren - overuren)
  if (effectieveNorm <= 0) return []

  const ratioProcent = round2((gewerkteUren / effectieveNorm) * 100)
  const { grenzen } = input
  const out: WeekstaatAnomalie[] = []

  if (
    grenzen.signaleerOndergrens &&
    grenzen.ondergrensProcent != null &&
    ratioProcent < grenzen.ondergrensProcent
  ) {
    out.push({
      code: 'UREN_TE_LAAG',
      ratioProcent,
      grensProcent: grenzen.ondergrensProcent,
      effectieveNorm,
    })
  }
  if (
    grenzen.signaleerBovengrens &&
    grenzen.bovengrensProcent != null &&
    ratioProcent > grenzen.bovengrensProcent
  ) {
    out.push({
      code: 'UREN_TE_HOOG',
      ratioProcent,
      grensProcent: grenzen.bovengrensProcent,
      effectieveNorm,
    })
  }
  return out
}
